/**
 * Chart panel container that orchestrates the chart toolbar, builder dialog, and renderer.
 * This is the main component rendered in the Chart tab.
 */

import { useState, useRef, useCallback } from 'react';
import { BarChart2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from '@/components/ui/toaster';
import type { QueryResultSet } from '../../../../types/queryExecution';
import type { ChartConfig, ChartType, ChartExportFormat } from '../types/chartTypes';
import { ChartToolbar } from './ChartToolbar';
import { ChartBuilderDialog } from './ChartBuilderDialog';
import { ChartRendererHost, type ChartRendererHandle } from './ChartRendererLazy';
import { useChartData } from '../hooks/useChartData';
import { exportChart } from '../services/chartExportApi';

type ChartPanelProps = {
  readonly resultSet: QueryResultSet | undefined;
  readonly chartConfig: ChartConfig | undefined;
  readonly onConfigChange: (config: ChartConfig | undefined) => void;
  /** Info note shown when chart is built from limited data (e.g., windowed mode) */
  readonly infoNote?: string;
  /** S20 — pin this chart (with its live config) to a dashboard. */
  readonly onPinToDashboard?: (chartConfig: ChartConfig) => void;
  /** Task 27 — precomputed spatial column ids (native + declared, excluding declared
   *  lat/lng PAIRs — see `ChartBuilderDialog`'s prop doc), forwarded to the builder
   *  dialog so spatial columns never leak into the axis/series pickers. */
  readonly spatialColumnIds?: ReadonlySet<string>;
};

export function ChartPanel({
  resultSet,
  chartConfig,
  onConfigChange,
  infoNote,
  onPinToDashboard,
  spatialColumnIds,
}: ChartPanelProps) {
  const [isBuilderOpen, setIsBuilderOpen] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const chartRef = useRef<ChartRendererHandle>(null);

  const { chartData, hasData, resolvedConfig } = useChartData(resultSet, chartConfig);

  const handleTypeChange = useCallback(
    (type: ChartType) => {
      if (!chartConfig) return;
      onConfigChange({ ...chartConfig, type });
    },
    [chartConfig, onConfigChange]
  );

  const handleExport = useCallback(
    async (format: ChartExportFormat) => {
      if (!chartRef.current) return;

      const svgContent = chartRef.current.getSvgContent();
      if (!svgContent) {
        toast.error({ title: 'Export failed', description: 'Could not get chart content' });
        return;
      }

      setIsExporting(true);
      try {
        const suggestedFileName = chartConfig?.title
          ? `${chartConfig.title.replace(/[^a-zA-Z0-9]/g, '-')}`
          : 'chart';

        const result = await exportChart(svgContent, format, suggestedFileName);

        if (result.success && result.filePath) {
          toast.success({ title: 'Chart exported', description: result.filePath });
        } else if (result.cancelled) {
          // User cancelled, no toast needed
        } else {
          toast.error({ title: 'Export failed', description: result.error || 'Unknown error' });
        }
      } catch (error) {
        toast.error({
          title: 'Export failed',
          description: error instanceof Error ? error.message : 'Unknown error',
        });
      } finally {
        setIsExporting(false);
      }
    },
    [chartConfig?.title]
  );

  const handleReset = useCallback(() => {
    onConfigChange(undefined);
  }, [onConfigChange]);

  const handleConfigure = useCallback(() => {
    setIsBuilderOpen(true);
  }, []);

  const handleConfigured = useCallback(
    (config: ChartConfig) => {
      onConfigChange(config);
    },
    [onConfigChange]
  );

  // No result set - show placeholder
  if (!resultSet || !Array.isArray(resultSet.rows) || resultSet.rows.length === 0) {
    // Distinguish "windowed buffer loading" (resultSet exists with columns) from "never executed"
    const isLoading = resultSet && Array.isArray(resultSet.columns) && resultSet.columns.length > 0;
    return (
      <div className="flex flex-col items-center justify-center h-full text-muted-foreground gap-2 p-8">
        <BarChart2 className="h-12 w-12 opacity-30" />
        <p className="text-sm">{isLoading ? 'Loading chart data...' : 'Run a query to create charts from results'}</p>
      </div>
    );
  }

  // No chart configured - show create button
  if (!chartConfig) {
    return (
      <div className="flex flex-col items-center justify-center h-full gap-4 p-8">
        <div className="text-center space-y-2">
          <BarChart2 className="h-12 w-12 mx-auto text-muted-foreground/50" />
          <p className="text-sm text-muted-foreground">
            Create a chart from your query results
          </p>
        </div>
        <Button
          onClick={() => setIsBuilderOpen(true)}
          className="gap-2"
          data-testid="create-chart-btn"
        >
          <BarChart2 className="h-4 w-4" />
          Create Chart
        </Button>
        <ChartBuilderDialog
          open={isBuilderOpen}
          onOpenChange={setIsBuilderOpen}
          columns={resultSet.columns}
          rows={resultSet.rows}
          onConfigured={handleConfigured}
          spatialColumnIds={spatialColumnIds}
        />
      </div>
    );
  }

  // Show empty data state
  if (!hasData) {
    return (
      <div className="flex flex-col items-center justify-center h-full gap-4 p-8">
        <p className="text-sm text-muted-foreground">
          No data available for the selected chart configuration
        </p>
        <Button variant="outline" onClick={handleConfigure}>
          Reconfigure Chart
        </Button>
      </div>
    );
  }

  // Show chart
  return (
    <div className="flex flex-col h-full" data-testid="chart-panel">
      {infoNote && (
        <div className="mx-4 mt-2 px-2 py-1.5 text-xs text-muted-foreground bg-muted/40 rounded border border-border">
          {infoNote}
        </div>
      )}
      <ChartToolbar
        currentType={chartConfig.type}
        onTypeChange={handleTypeChange}
        onExport={handleExport}
        onReset={handleReset}
        onConfigure={handleConfigure}
        isExporting={isExporting}
        onPinToDashboard={onPinToDashboard ? () => onPinToDashboard(chartConfig) : undefined}
      />

      <div className="flex-1 p-4 overflow-auto">
        <ChartRendererHost
          ref={chartRef}
          data={chartData}
          config={resolvedConfig ?? chartConfig}
          height={400}
        />
      </div>

      <ChartBuilderDialog
        open={isBuilderOpen}
        onOpenChange={setIsBuilderOpen}
        columns={resultSet.columns}
        rows={resultSet.rows}
        existingConfig={chartConfig}
        onConfigured={handleConfigured}
        spatialColumnIds={spatialColumnIds}
      />
    </div>
  );
}
